import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { LookupOptions } from '../../core/util/lookup-options';
import { NavigationService } from '../../service/core/navigation.service';
import { UppComponent, UppComponentNames, UppViewNames } from '../../service/model';
import { UserDetailsService } from '../../service/user-details.service';
import {
  PERMISSION_MANAGE_EDIFACT_RULE,
  PERMISSION_MANAGE_NDC_RULE,
  PERMISSION_VIEW_MARKET,
  PERMISSION_VIEW_POS
} from '../../service/user-permissions.service';
import { selectAllAvailableMarketsRecords } from '../../store/markets/markets-selector';
import { selectAllAvailablePosRecords } from '../../store/pos/pos-selector';
import { JourneyUi, JourneyUiSubRule, initialJourneyUi } from '../model';
import { Subscription } from 'rxjs';
import { marketPairsUniqueValidator } from '../../components/form-validators/market-pairs-unique.validator';
import { ButtonGroupItem, hasActiveButtonItem } from '../../model/button-group-item';
import { excludeSubruleShouldBePresent } from './validators/subrule-validators';
import { ContentType, Product } from '../model/journey';
import { ValueLabelItem } from '../../model/value-label-item';
import {
  MAX_LENGTH_STRING_VALIDATION_MESSAGES,
  NAME_WITH_SPACES_VALIDATION_MESSAGES,
  SECTION_VALIDATION_MESSAGES
} from '../../service/model/common/validation-messages';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { FeatureFlags } from '../../core/util/resources';
import { RuleDateRangeComponent } from '../../components/rule-date-range/rule-date-range.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { DfInsertIconDirective } from '@design-factory/design-factory';
import { NgbTooltip, NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { ButtonGroupComponent } from '../../components/button-group/button-group.component';
import { CheckboxMultiselectComponent } from '../../components/checkbox-multiselect/checkbox-multiselect.component';
import { LookupSelectComponent } from '../../components/lookup-select/lookup-select.component';
import { ValidationErrorMessageComponent } from '../../components/validation-error-message/validation-error-message.component';
import { FlightDetailsCarrierComponent } from './components/flight-details-carrier/flight-details-carrier.component';
import { FareContentComponent } from './fare-content/fare-content.component';
import { JourneyContentComponent } from './journey-content/journey-content.component';
import { RouteContentComponent } from './route-content/route-content.component';
import { RuleActionOrValueComponent } from './rule-action-or-value/rule-action-or-value.component';
import { MarketPair } from '../../model/market-pair';

const NAME_REGEX = '^[A-Z0-9 ]{0,30}$';

const MARKET_LOOKUP_OPTIONS: LookupOptions = {
  destinationComponent: UppComponentNames.MARKETS,
  sourceComponent: UppComponentNames.FLIGHTS,
  sourceView: UppViewNames.CREATE
};

const SCHEDULE_DRIVEN_FLOWS = 'schedule-driven';
const SHOPPING_FLOWS = 'shopping';
const AVAILABILITY_AND_SCHEDULES_LABEL = $localize`:@@upp.flights.product.availabilityAndSchedules:Availability and schedules`;
const SHOPPING_SEARCHES_LABEL = $localize`:@@upp.flights.product.shoppingSearches:Shopping searches`;
@Component({
  selector: 'ama-ng-upp-flights-details',
  templateUrl: './flights-details.component.html',
  styleUrls: ['./flights-details.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RuleDateRangeComponent,
    NgbTooltip,
    NgClass,
    ValidationErrorMessageComponent,
    LookupSelectComponent,
    NgTemplateOutlet,
    CheckboxMultiselectComponent,
    NgbAlert,
    DfInsertIconDirective,
    ButtonGroupComponent,
    FlightDetailsCarrierComponent,
    RouteContentComponent,
    JourneyContentComponent,
    FareContentComponent,
    RuleActionOrValueComponent
  ]
})
export class FlightsDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() flightDetailsForm: UntypedFormGroup | null = null;
  @Input() flightDetails: JourneyUi = initialJourneyUi;
  @Input() readonly = true;
  @Input() parent!: UppComponent;

  @ViewChild(RuleDateRangeComponent) ruleDateRange!: RuleDateRangeComponent;

  readonly MAX_NUMBER_OF_MARKET_PAIRS = 10;
  readonly COMPONENT_NAME = UppComponentNames.FLIGHTS;

  NAME_WITH_SPACES_VALIDATION_MESSAGES = NAME_WITH_SPACES_VALIDATION_MESSAGES;
  MAX_LENGTH_STRING_VALIDATION_MESSAGES = MAX_LENGTH_STRING_VALIDATION_MESSAGES;
  SECTION_VALIDATION_MESSAGES = SECTION_VALIDATION_MESSAGES;

  contentValidationMessages = {
    excludeSubruleRequired: () =>
      $localize`:@@upp.validation.flights.rule.actionOrValue.addExcludeRule:Add a rule content with Exclude action`,
    sortValueOutOfRange: (params: ValidationErrors) =>
      $localize`:@@upp.validation.flights.rule.actionOrValue.sortValueOutOfRange:Minimum/maximum value exceeded: ${params.min} to +${params.max}`,
    required: () => $localize`:@@upp.validation.flights.content.section.required:Section is required`,
    sortValueRequired: () => $localize`:@@upp.validation.required:Field is required`,
    sortValueNotInteger: () =>
      $localize`:@@upp.validation.flights.rule.actionOrValue.sortValueNotInteger:Sort value must be an integer`
  };
  marketPairsValidationMessages = {
    duplicateMarketPair: () =>
      $localize`:@@upp.validation.flights.applicability.marketPairs.duplicate:Market pair already exists`
  };

  posLookupOptions!: LookupOptions;

  availablePosNames: string[] = [];
  availableMarketsNames: string[] = [];

  subRuleDefinitions: SubRuleDefinition[] = [];

  airContentTypeItems: ValueLabelItem<string>[] = [];
  hasNDCContentType = false;
  hasEdifactContentType = true;
  showFlightsWarningMessage = false;
  ruleActiveRange: boolean | null = null;
  flightsNdcParity: boolean | null = null;
  flightsProductField: boolean | null = null;
  productInstantSearch: boolean | null = null;
  flightsUiChanges: boolean | null = null;
  excludeIntracityConnectionsEnabled: boolean | null = null;
  productItems: { value: string; label: string; parent?: string }[] = [];

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly userDetailsService: UserDetailsService,
    private readonly store: Store,
    private readonly navigationService: NavigationService,
    private readonly configurationService: ConfigurationService
  ) {}

  get name(): AbstractControl | null {
    return this.flightDetailsForm?.get('rule.name') ?? null;
  }

  get description(): AbstractControl | null {
    return this.flightDetailsForm?.get('rule.description') ?? null;
  }

  get active(): AbstractControl | null {
    return this.flightDetailsForm?.get('rule.active') ?? null;
  }

  get marketPairs(): FormArray | null {
    const control = this.flightDetailsForm?.get('applicability.marketPairs');
    return control ? (control as FormArray) : null;
  }

  get marketPairsControls(): FormGroup[] {
    return (this.marketPairs?.controls as FormGroup[]) ?? [];
  }

  get applicability(): UntypedFormGroup | undefined {
    const control = this.flightDetailsForm?.get('applicability');
    return control ? (control as UntypedFormGroup) : undefined;
  }

  get contentTypes(): AbstractControl | null {
    return this.applicability?.get('contentTypes') ?? null;
  }

  get subRules(): FormArray | null {
    const control = this.flightDetailsForm?.get('content.subRules');
    return control ? (control as FormArray) : null;
  }

  get products(): AbstractControl | null {
    return this.flightDetailsForm?.get('applicability.products') ?? null;
  }

  get areBothContentTypesSelected(): boolean {
    return this.hasEdifactContentType && this.hasNDCContentType;
  }

  get showWarningMessage(): boolean {
    return this.showFlightsWarningMessage && !this.readonly;
  }

  get warningMessage(): string {
    if (this.areBothContentTypesSelected && !this.flightsNdcParity) {
      return $localize`:@@upp.flights.yy.message.NDC.edifact:If you don't select anything in the field under carrier or route, all the options will be added to the rule.`;
    }
    if (this.hasEdifactContentType || (this.areBothContentTypesSelected && this.flightsNdcParity)) {
      return $localize`:@@upp.flights.yy.message.edifact:If you don't select anything in the field under carrier, route or journey, all the options will be added to the rule.`;
    }
    if (this.hasNDCContentType && this.flightsNdcParity) {
      return $localize`:@@upp.flights.yy.message.NDC.parity:If you don't select anything in the field under carrier, route, journey or fare, all the options will be added to the rule.`;
    }
    return $localize`:@@upp.flights.yy.message.NDC:If you don't select anything in the field under carrier, route or fare, all the options will be added to the rule.`;
  }

  get carrierButtonGroupItem(): ButtonGroupItem {
    return {
      name: 'carrier',
      active: false,
      disabled: this.readonly,
      title: $localize`:@@upp.global.criteria.carrier.label:Carrier`
    };
  }

  get routeButtonGroupItem(): ButtonGroupItem {
    return {
      name: 'route',
      active: false,
      disabled: this.readonly,
      title: $localize`:@@upp.global.criteria.route.label:Route`
    };
  }

  get journeyButtonGroupItem(): ButtonGroupItem {
    return {
      name: 'journey',
      active: false,
      disabled: this.readonly,
      title: $localize`:@@upp.global.criteria.journey.label:Journey`
    };
  }

  get fareButtonGroupItem(): ButtonGroupItem {
    return {
      name: 'fare',
      active: false,
      disabled: this.readonly,
      title: $localize`:@@upp.global.criteria.fare.label:Fare`
    };
  }

  getSubRuleDefinitionFormGroup(subRule: SubRuleDefinition, groupName: string): FormGroup {
    return subRule.formGroup.get(groupName) as FormGroup;
  }

  productGroupValueFn = (groupName: string) => {
    if (groupName === AVAILABILITY_AND_SCHEDULES_LABEL) {
      return {
        value: SCHEDULE_DRIVEN_FLOWS,
        label: AVAILABILITY_AND_SCHEDULES_LABEL
      };
    } else if (groupName === SHOPPING_SEARCHES_LABEL) {
      return {
        value: SHOPPING_FLOWS,
        label: SHOPPING_SEARCHES_LABEL
      };
    }
    return { value: '', label: '' };
  };

  ngOnInit(): void {
    this.airContentTypeItems = [
      {
        value: ContentType.EDIFACT,
        label: 'EDIFACT'
      },
      {
        value: ContentType.NDC,
        label: 'NDC'
      }
    ];

    this.productItems = this.flightsProductField
      ? [
          {
            value: Product.AVL,
            label: $localize`:@@upp.flights.product.availabilityWebServices:Availability (AN/SN/TN) and Availability Web Services`,
            parent: AVAILABILITY_AND_SCHEDULES_LABEL
          },
          {
            value: Product.SP,
            label: this.productInstantSearch
              ? $localize`:@@upp.flights.product.masterPricerExpert:Master Pricer Expert (FXD)`
              : $localize`:@@upp.flights.product.fxdEntries:FXD entries (cryptic)`,
            parent: SHOPPING_SEARCHES_LABEL
          },
          {
            value: Product.MP,
            label: $localize`:@@upp.flights.product.allFaresMasterPricerWebServices:All Fares and Master Pricer Travelboard Web Services`,
            parent: SHOPPING_SEARCHES_LABEL
          }
        ]
      : [
          {
            value: Product.AVL,
            label: $localize`:@@upp.flights.product.availability:Availability`
          },
          {
            value: Product.MPE,
            label: $localize`:@@upp.flights.product.masterPricerExpert:Master Pricer Expert`
          },
          {
            value: Product.MP,
            label: $localize`:@@upp.flights.product.masterPricerTravelboard:Master Pricer Travelboard`
          }
        ];

    if (this.productInstantSearch) {
      this.productItems.push(
        this.flightsProductField
          ? {
              value: Product.MPX,
              label: $localize`:@@upp.flights.product.masterPricerXWebServices:Master Pricer X Web Services`,
              parent: SHOPPING_SEARCHES_LABEL
            }
          : {
              value: Product.MPX,
              label: $localize`:@@upp.flights.product.masterPricerX:Master Pricer X`
            }
      );
    }

    if (this.flightsProductField && this.productInstantSearch) {
      this.productItems.push({
        value: Product.MPISWS,
        label: $localize`:@@upp.flights.product.MPInstantSearchWebServices:Master Pricer Instant Search Web Services`,
        parent: SHOPPING_SEARCHES_LABEL
      });
    }

    if (this.canLookupPos()) {
      this.posLookupOptions = {
        destinationComponent: UppComponentNames.POS,
        sourceComponent: UppComponentNames.FLIGHTS,
        sourceView: this.flightDetails.rule.id ? UppViewNames.MODIFY : UppViewNames.CREATE
      };
    }

    this.subscription.add(
      this.store.pipe(select(selectAllAvailablePosRecords)).subscribe((result) => {
        if (result != null) {
          this.availablePosNames = result
            .map((posMarketRecord) => posMarketRecord.posMarketDetail?.name)
            .filter((name) => name !== undefined) as string[];
        }
      })
    );

    this.subscription.add(
      this.store.pipe(select(selectAllAvailableMarketsRecords)).subscribe((result) => {
        if (result != null) {
          this.availableMarketsNames = result
            .map((posMarketRecord) => posMarketRecord.posMarketDetail?.name)
            .filter((name) => name !== undefined) as string[];
        }
      })
    );

    this.navigationService.enableNavigation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.ruleActiveRange)?.subscribe((ruleActiveRange) => {
        this.ruleActiveRange = ruleActiveRange ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.flightsNdcParity)?.subscribe((flightsNdcParity) => {
        this.flightsNdcParity = flightsNdcParity ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.flightsProductField)?.subscribe((flightsProductField) => {
        this.flightsProductField = flightsProductField ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.productInstantSearch)?.subscribe((productInstantSearch) => {
        this.productInstantSearch = productInstantSearch ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.flightsUiChanges)?.subscribe((flightsUiChanges) => {
        this.flightsUiChanges = flightsUiChanges ?? false;
      })
    );

    this.subscription.add(
      this.configurationService
        .getParameter$(FeatureFlags.excludeIntracityConnections)
        ?.subscribe((excludeIntracityConnections) => {
          this.excludeIntracityConnectionsEnabled = excludeIntracityConnections ?? false;
        })
    );

    if (changes.flightDetails) {
      this.flightDetails = JSON.parse(JSON.stringify(changes.flightDetails.currentValue));
      this.initializeForm();
    }
  }

  clearScreen(): void {
    // Keep the name in case of Update (Modify, Display)
    const name = this.parent === UppComponent.FLIGHTS_CREATE ? '' : this.flightDetails.rule.name;
    const id = this.flightDetails.rule.id;
    if (this.ruleActiveRange) {
      this.ruleDateRange.clearDateRange();
    }
    this.flightDetails = JSON.parse(
      JSON.stringify({
        ...initialJourneyUi,
        rule: {
          ...initialJourneyUi.rule,
          name,
          id
        }
      })
    );

    this.showFlightsWarningMessage = false;

    // Completely destroy and recreate the form to ensure validation components re-initialize
    const tempForm = this.flightDetailsForm;
    this.flightDetailsForm = null;

    // Allow Angular to detect the change and destroy validation components
    setTimeout(() => {
      this.flightDetailsForm = tempForm;
      // Reinitialize the entire form with cleared data
      this.initializeForm();
    }, 0);
  }

  getMarketLookupOptions(): LookupOptions | undefined {
    if (this.canLookupMarket()) {
      return {
        ...MARKET_LOOKUP_OPTIONS,
        sourceView: this.flightDetails.rule.id ? UppViewNames.MODIFY : UppViewNames.CREATE
      };
    }
    return undefined;
  }

  addBidirectionalMarketPair(atPosition: number, originalMarketPair: MarketPair): void {
    this.addMarketPair(atPosition, {
      firstMarketName: originalMarketPair.secondMarketName,
      secondMarketName: originalMarketPair.firstMarketName
    });
  }

  addMarketPair(atPosition?: number, marketPair?: MarketPair): void {
    const marketPairFormGroup = this.formBuilder.group(
      {
        firstMarketName: [{ value: marketPair?.firstMarketName, disabled: this.readonly }, [Validators.required]],
        secondMarketName: [{ value: marketPair?.secondMarketName, disabled: this.readonly }, [Validators.required]]
      },
      { validators: marketPairsUniqueValidator() }
    );

    // Touch to force display duplicated validation error if any
    marketPairFormGroup.markAsTouched();

    this.subscription.add(
      marketPairFormGroup.valueChanges.subscribe(() => {
        this.validateMarketPairs(marketPairFormGroup);
      })
    );
    this.marketPairs?.insert(atPosition ?? this.marketPairs?.length ?? 0, marketPairFormGroup);

    this.validateMarketPairs();
  }

  removeMarketPair(index: number): void {
    this.marketPairs?.removeAt(index);
    this.validateMarketPairs();
  }

  addSubRule(atPosition?: number, subRule?: JourneyUiSubRule): void {
    const subRuleFormGroup = this.formBuilder.group({
      hasRuleContent: this.formBuilder.control(false, Validators.requiredTrue),
      applicability: this.formBuilder.group({}),
      content: this.formBuilder.control({ value: subRule?.content, disabled: this.readonly })
    });

    this.subRules?.insert(atPosition ?? this.subRules?.length ?? 0, subRuleFormGroup);

    const ruleContentButtonGroupItems = this.generateRuleContentButtonGroupItems();
    const subRuleDefinition: SubRuleDefinition = {
      ruleContentButtonGroupItems,
      ruleContentButtonGroupMap: ruleContentButtonGroupItems.reduce(
        (acc: { [key: string]: ButtonGroupItem }, item: ButtonGroupItem) => {
          acc[item.name] = item;
          return acc;
        },
        {}
      ),
      formGroup: subRuleFormGroup
    };

    if (subRule) {
      // Clone sub rule to make it modifiable.
      subRuleDefinition.subRule = JSON.parse(JSON.stringify(subRule));
    }

    this.subRuleDefinitions.splice(atPosition ?? this.subRules?.length ?? 0, 0, subRuleDefinition);
  }

  removeSubRule(index: number): void {
    this.subRules?.removeAt(index);
    this.subRuleDefinitions.splice(index, 1);
  }

  onRuleContentToggle(buttonGroupItem: ButtonGroupItem, subRuleDefinition: SubRuleDefinition): void {
    const applicabilityFormGroup = subRuleDefinition.formGroup.get('applicability') as FormGroup;
    if (buttonGroupItem.active) {
      applicabilityFormGroup.addControl(buttonGroupItem.name, this.formBuilder.group({}));
    } else {
      applicabilityFormGroup.removeControl(buttonGroupItem.name);
      // Once collapsed section (scope) data should be cleared. Otherwise if expanded again it will be prefilled (in case of modify).
      delete subRuleDefinition.subRule?.applicability?.[
        buttonGroupItem.name as keyof typeof subRuleDefinition.subRule.applicability
      ];
    }

    const hasRuleContentControl = subRuleDefinition.formGroup.get('hasRuleContent');
    hasRuleContentControl?.setValue(hasActiveButtonItem(subRuleDefinition.ruleContentButtonGroupItems));
    hasRuleContentControl?.markAsTouched();
    this.showFlightsWarningMessage = hasRuleContentControl?.value;
  }

  onAirContentTypeChange(): void {
    // Clear the data in case we are in modify mode.
    if (this.flightDetails?.content?.subRules) {
      this.flightDetails.content.subRules = [];
    }
    this.showFlightsWarningMessage = false;
    this.initializeSubRules();
  }

  ngOnDestroy(): void {
    this.flightDetailsForm?.reset({});
    this.subscription.unsubscribe();
  }

  private validateMarketPairs(marketPairToSkip?: UntypedFormGroup): void {
    this.marketPairs?.controls.forEach((control) => {
      if (!marketPairToSkip || control !== marketPairToSkip) {
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private canLookupPos(): boolean {
    return this.canLookup(PERMISSION_VIEW_POS);
  }

  private canLookupMarket(): boolean {
    return this.canLookup(PERMISSION_VIEW_MARKET);
  }

  private canLookup(permisionName: string): boolean {
    if (this.readonly) {
      return false;
    }
    const permissions = this.userDetailsService.loggedInUser?.permissions;
    return permissions ? permissions.filter((p) => p === permisionName).length > 0 : false;
  }

  private initializeForm(): void {
    if (this.flightDetailsForm) {
      this.flightDetailsForm.setControl(
        'rule',
        this.formBuilder.group({
          id: [this.flightDetails?.rule?.id],
          version: [this.flightDetails?.rule?.version],
          organization: [this.userDetailsService.getLssOrganization()],
          name: [
            {
              value: this.flightDetails?.rule?.name,
              disabled: this.readonly || this.parent !== UppComponent.FLIGHTS_CREATE
            },
            [Validators.required, Validators.maxLength(30), Validators.pattern(NAME_REGEX)]
          ],
          description: [
            { value: this.flightDetails?.rule?.description, disabled: this.readonly },
            [Validators.maxLength(128)]
          ],
          active: [{ value: this.flightDetails?.rule?.active, disabled: this.readonly }]
        })
      );

      const applicabilityGroup = this.formBuilder.group({
        pointOfSaleNames: [
          {
            value: this.flightsUiChanges
              ? this.flightDetails?.applicability?.pointOfSaleNames
              : (this.flightDetails?.applicability?.pointOfSaleNames?.[0] ?? null),
            disabled: this.readonly
          },
          [Validators.required]
        ],
        marketPairs: this.formBuilder.array([], [Validators.required])
      });

      if (this.ruleActiveRange) {
        applicabilityGroup.addControl(
          'validityApplicationPeriods',
          this.formBuilder.array([
            this.formBuilder.group({
              start: this.formBuilder.control(
                this.flightDetails?.applicability?.validityApplicationPeriods?.[0]?.start
              ),
              end: this.formBuilder.control(this.flightDetails?.applicability?.validityApplicationPeriods?.[0]?.end)
            })
          ])
        );

        applicabilityGroup.addControl(
          'validityApplicationWeekdays',
          this.formBuilder.control(this.flightDetails?.applicability?.validityApplicationWeekdays)
        );

        this.ruleDateRange?.setCalendarValues(
          this.flightDetails?.applicability?.validityApplicationPeriods?.[0]?.start ?? '',
          this.flightDetails?.applicability?.validityApplicationPeriods?.[0]?.end ?? null
        );
      }

      this.flightDetailsForm.setControl('applicability', applicabilityGroup);

      this.initializeContentTypes();

      if (this.hasEdifactContentType && !this.hasNDCContentType) {
        this.applicability?.setControl(
          'products',
          this.formBuilder.control(
            {
              value: this.flightDetails?.applicability?.products,
              disabled: this.readonly
            },
            this.flightsProductField ? [] : [Validators.required]
          )
        );
      }

      this.flightDetailsForm.setControl(
        'content',
        this.formBuilder.group({
          subRules: this.formBuilder.array([], excludeSubruleShouldBePresent)
        })
      );

      this.showFlightsWarningMessage = false;
      this.initializeMarketPairs();
      this.initializeSubRules();
    }
  }

  private initializeContentTypes(): void {
    let initialContentTypes: ContentType[];
    if (this.flightDetails.rule.id) {
      // In case that entity was created before adding content types property.
      initialContentTypes = this.flightDetails?.applicability?.contentTypes ?? [ContentType.EDIFACT];
    } else {
      initialContentTypes = this.getDefaultContentTypes(false);
    }
    // Content type is disabled if user doesn't have both permissions.
    const disableContentType = !this.hasManageEdifactPermission() || !this.hasManageNDCPermission();

    const contentTypesControl = this.formBuilder.control(
      {
        value: initialContentTypes,
        disabled: this.readonly || disableContentType
      },
      [Validators.required]
    );

    this.hasNDCContentType = initialContentTypes.includes(ContentType.NDC);
    this.hasEdifactContentType = initialContentTypes.includes(ContentType.EDIFACT);

    this.subscription.add(
      contentTypesControl.valueChanges.subscribe((val) => {
        // Keep in local variable to avoid calling a method each time.
        this.hasNDCContentType = val?.includes(ContentType.NDC);
        this.hasEdifactContentType = val?.includes(ContentType.EDIFACT);

        if (this.hasNDCContentType || !this.hasEdifactContentType) {
          this.applicability?.removeControl('products');
        } else {
          this.applicability?.setControl(
            'products',
            this.formBuilder.control(
              {
                value: this.flightDetails?.applicability?.products ?? [],
                disabled: this.readonly
              },
              this.flightsProductField ? [] : [Validators.required]
            )
          );
        }
      })
    );

    this.applicability?.setControl('contentTypes', contentTypesControl);
  }

  private hasManageEdifactPermission(): boolean {
    return this.userDetailsService.loggedInUser?.permissions?.includes(PERMISSION_MANAGE_EDIFACT_RULE) ?? false;
  }

  private hasManageNDCPermission(): boolean {
    return this.userDetailsService.loggedInUser?.permissions?.includes(PERMISSION_MANAGE_NDC_RULE) ?? false;
  }

  private initializeMarketPairs() {
    this.marketPairs?.clear();

    if (this.flightDetails.applicability?.marketPairs) {
      const marketPairs = this.flightDetails.applicability.marketPairs;

      for (const marketPair of marketPairs) {
        this.addMarketPair(undefined, marketPair);
      }
    } else {
      this.addMarketPair();
    }
  }

  private initializeSubRules() {
    this.subRuleDefinitions.splice(0, this.subRuleDefinitions.length);
    this.subRules?.clear();
    this.initializeSections();
  }

  private generateRuleContentButtonGroupItems(): ButtonGroupItem[] {
    const buttonGroupItems = [this.carrierButtonGroupItem, this.routeButtonGroupItem];
    if (this.isSingleContentTypeSelected(ContentType.EDIFACT) || this.flightsNdcParity) {
      buttonGroupItems.push(this.journeyButtonGroupItem);
    }

    if (this.isSingleContentTypeSelected(ContentType.NDC)) {
      buttonGroupItems.push(this.fareButtonGroupItem);
    }

    return buttonGroupItems;
  }

  private isSingleContentTypeSelected(contentType: ContentType): boolean {
    const selectedContentTypes = this.contentTypes?.value;
    return selectedContentTypes?.length === 1 && selectedContentTypes[0] === contentType;
  }

  private initializeSections(): void {
    if ((this.flightDetails?.content?.subRules ?? []).length > 0) {
      this.flightDetails.content?.subRules.forEach((subRule, index) => {
        this.addSubRule(this.subRules?.length, subRule);
        const subRuleDefinition = this.subRuleDefinitions[index];
        this.processSubRuleApplicability(subRule, subRuleDefinition);
      });
    } else if (this.contentTypes?.value?.length > 0) {
      this.addSubRule();
    }
  }

  private processSubRuleApplicability(subRule: JourneyUiSubRule, subRuleDefinition: SubRuleDefinition): void {
    const applicabilityTypes = ['carrier', 'route', 'journey', 'fare'] as const;
    applicabilityTypes.forEach((applicabilityType) => {
      if (subRule.applicability[applicabilityType]) {
        subRuleDefinition.ruleContentButtonGroupMap[applicabilityType].active = true;
        this.onRuleContentToggle(subRuleDefinition.ruleContentButtonGroupMap[applicabilityType], subRuleDefinition);
      }
    });
  }

  private getDefaultContentTypes(clear: boolean): ContentType[] {
    if (this.hasManageEdifactPermission() && this.hasManageNDCPermission()) {
      return clear ? [] : (this.flightDetails?.applicability?.contentTypes ?? []);
    } else if (this.hasManageEdifactPermission()) {
      return [ContentType.EDIFACT];
    } else if (this.hasManageNDCPermission()) {
      return [ContentType.NDC];
    }
    return [];
  }
}

interface SubRuleDefinition {
  ruleContentButtonGroupItems: ButtonGroupItem[];
  ruleContentButtonGroupMap: { [key: string]: ButtonGroupItem };

  formGroup: FormGroup;
  subRule?: JourneyUiSubRule;
}
